/* eslint-disable arrow-body-style */
 
import  {  useCallback } from "react";
import _ from 'lodash';
import { useDependencies } from "@xengage/gw-portals-dependency-react";
import { useAuthentication } from "@xengage/gw-digital-auth-react";
import { useModal } from '@jutro/components';
import { PremierConstants } from 'premier-constant-service';
import { messages as commonMessages } from '@xengage/gw-platform-translations';

// Use CommonService for validateSubmission
import { LoadSaveService as CommonService } from 'gw-capability-gateway-quoteandbind';

function useUpdateSubmissionServices() {
    const { showAlert } = useModal();
    const { LoadSaveService, EndorsementService,RenewalService } = useDependencies(['LoadSaveService', 'EndorsementService','RenewalService']);
    const { jobTypes } = PremierConstants;
    const { authHeader } = useAuthentication();
    const errorMessage = 'This quote will require underwriting approval prior to issuance.';
    const definedErrors = {
        errors: [{ 'at least one schedule item': 'Coverages with scheduled items in the quote must contain at least one scheduled item' }]
    };


    /**
     * 
     * @param {*} productCode clause group object 
     * @param {*} whichPage
     * @param {*} submissionVMsubmissionVM
     */
    const updateSubmission = useCallback( async (productCode, whichPage, submissionVM, customValidationLevel = undefined) => {
        let response;
        const submissionVMValue = _.cloneDeep(submissionVM.value);

        _.unset(submissionVMValue, "bindData");

        if (submissionVMValue.baseData.jobType === jobTypes.policyChange) {
            response = await EndorsementService
                .saveEndorsement([submissionVMValue], authHeader);
        }else if(submissionVMValue.baseData.jobType === jobTypes.renewal) {
            response = await RenewalService.saveRenewal([submissionVMValue], authHeader);
        }else {
            response = await LoadSaveService.updateDraftSubmission(
                submissionVMValue,
                authHeader
            );
        }

        return response;
    }, [EndorsementService, LoadSaveService, RenewalService, authHeader, jobTypes]);

    /**
     * @param {*} productCode clause group object 
     * @param {*} whichPage
     * @param {*} submissionVM
     * @param {*} customValidationLevel
     */
    const validateSubmission = useCallback( async (productCode, whichPage, jobNumber, submissionVM, customValidationLevel = 'default') => {
        const errorResponse = await CommonService.validate(jobNumber, whichPage, customValidationLevel, authHeader);

        if (errorResponse) {
            _.set(submissionVM, 'value.errorsAndWarnings', errorResponse);
        }

        return submissionVM.value;
    }, [authHeader]);

    /**
     * @param {*} error
     */
    const showExceptionMessage = useCallback(async() => {
        await showAlert({
            title: commonMessages.genericErrorMessage,
            message: errorMessage,
            status: 'error',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.ok
        }).catch(_.noop);
      
    }, [showAlert]);

    /**
     * @param {*} productCode clause group object 
     * @param {*} whichPage
     * @param {*} jobNumber
     * @param {*} submissionVM
     * @param {*} jobType
     */
    const handleBindUWIssues = useCallback(async (productCode, whichPage, jobNumber, submissionVMValue, jobType) => {
         
        const underwritingIssues = _.get(submissionVMValue, 'errorsAndWarnings.underwritingIssues');
        const isBindBlocked = underwritingIssues?.some((uwIssue) => uwIssue.currentBlockingPoint === 'BlocksBind');
        const isBindBlocksIssuance = underwritingIssues?.some((uwIssue) => uwIssue.currentBlockingPoint === 'BlocksIssuance');

        if(isBindBlocked || isBindBlocksIssuance) {
            let id;
            let summaryPath;

            await showExceptionMessage();

            switch (jobType) {
                case "PolicyChange":
                    id = submissionVMValue.jobID;
                    summaryPath = 'change';
                    break;
                case "Renewal":
                    id = submissionVMValue.jobID;
                    summaryPath = 'renewal';
                    break;
                case "Submission":
                default:
                    id = submissionVMValue.quoteID;
                    summaryPath = 'quotes';
                    break;
            }

            // const jumpLocation = `/producer-engage/${summaryPath}/${id}/summary`;
            const jumpLocation = `/${summaryPath}/${id}/summary`;

            return jumpLocation;
        }

        return undefined;
    }, [showExceptionMessage]);

    /**
     * @param {*} productCode clause group object 
     * @param {*} whichPage
     * @param {*} submissionVM
     * @param {*} customValidationLevel
     */
    const handleBindProcess = useCallback(async (productCode, whichPage, jobNumber, submissionVMValue, paymentMethodType, jobType, billingContactIdExt = undefined) => {
        const handleBindResponse = { jumpLocation: undefined , bindResponse: undefined };
    
        switch (jobType) {
            case "PolicyChange":
                await EndorsementService.bind([jobNumber, { paymentMethod: paymentMethodType, billingContactId_Ext: billingContactIdExt }], authHeader)
                    .then(async (result) => {
                        handleBindResponse.bindResponse = result;

                        const jumpLocationDueToUWIssue = await handleBindUWIssues(productCode, whichPage, jobNumber, result, jobType);

                        if(!_.isUndefined(jumpLocationDueToUWIssue)){
                            handleBindResponse.jumpLocation = jumpLocationDueToUWIssue;
                        }
                     
                    }).catch(async () => {
                        await showExceptionMessage();
                        // handleBindResponse.jumpLocation = `/producer-engage/change/${jobNumber}/summary`;
                        handleBindResponse.jumpLocation = `/change/${jobNumber}/summary`;
                    }).finally(() => {
                        return handleBindResponse;
                    });
                break;
            case "Renewal":
                await LoadSaveService.bind([jobNumber, { paymentMethod: paymentMethodType }], authHeader)
                    .then(async (result) => {
                        handleBindResponse.bindResponse = result;

                        const jumpLocationDueToUWIssue = await handleBindUWIssues(productCode, whichPage, jobNumber, result, jobType);

                        if(!_.isUndefined(jumpLocationDueToUWIssue)){
                            handleBindResponse.jumpLocation = jumpLocationDueToUWIssue;
                        }
                     
                    }).catch(async () => {
                        await showExceptionMessage();
                       // handleBindResponse.jumpLocation = `/producer-engage/renewal/${jobNumber}/summary`;
                       handleBindResponse.jumpLocation = `/renewal/${jobNumber}/summary`;
                    }).finally(() => {
                        return handleBindResponse;
                    });
                break;
            case "Submission":
            default:
                await LoadSaveService.bindSubmission(submissionVMValue, authHeader)
                    .then(async (result) => {
                        handleBindResponse.bindResponse = result;

                        const jumpLocationDueToUWIssue = await handleBindUWIssues(productCode, whichPage, jobNumber, result, jobType);

                        if(!_.isUndefined(jumpLocationDueToUWIssue)){
                            handleBindResponse.jumpLocation = jumpLocationDueToUWIssue;
                        }
                     
                    }).catch(async () => {
                        await showExceptionMessage();
                        // handleBindResponse.jumpLocation = `/producer-engage/quotes/${jobNumber}/summary`;
                        handleBindResponse.jumpLocation = `/quotes/${jobNumber}/summary`
                    }).finally(() => {
                        return handleBindResponse;
                    });
                break;
        }

        return handleBindResponse;
    }, [EndorsementService, LoadSaveService, authHeader, handleBindUWIssues, showExceptionMessage]);

    /**
     * @param {*} productCode clause group object 
     * @param {*} whichPage
     * @param {*} jobNumber
     * @param {*} submissionVM
     * @param {*} jobType
     */
    const handleUWIssues = useCallback(async (productCode, whichPage, jobNumber, submissionVMValue, jobType) => {
         
        const underwritingIssues = _.get(submissionVMValue, 'errorsAndWarnings.underwritingIssues');
        const isQuoteBlocked = underwritingIssues?.some((uwIssue) => uwIssue.currentBlockingPoint === "BlocksQuote");
        
        if (isQuoteBlocked) {
            let id;
            let summaryPath;

            await showExceptionMessage();

            switch (jobType) {
                case "PolicyChange":
                    id = submissionVMValue.jobID;
                    summaryPath = 'change';
                    break;
                case "Renewal":
                    id = submissionVMValue.jobID;
                    summaryPath = 'renewal';
                    break;
                case "Submission":
                default:
                    id = submissionVMValue.quoteID;
                    summaryPath = 'quotes';
                    break;
            }

            // const jumpLocation = `/producer-engage/${summaryPath}/${id}/summary`;
            const jumpLocation = `/${summaryPath}/${id}/summary`;
           
            return jumpLocation;
        }

        return undefined;
    }, [showExceptionMessage]);

    /**
     * 
     * @param {*} productCode clause group object 
     * @param {*} whichPage
     * @param {*} submissionVMsubmissionVM
     */
    const saveAndQuoteSubmissionService = useCallback( async (productCode, whichPage, jobNumber, submissionVMValue, jobType, isDirty = true) => {
        const handleQuoteResponse = { jumpLocation: undefined , quoteResponse: undefined };
        
        _.unset(submissionVMValue, 'bindData');

        if(submissionVMValue.baseData.jobType === 'PolicyChange') {
            // Policy change quote service is not saving the data so save service needs to be called
            // To save discounts data
            if(isDirty) {
                await EndorsementService.saveEndorsement([submissionVMValue], authHeader);
            }

            await EndorsementService.quoteEndorsement([jobNumber], authHeader)
                .then(async (result) => {
                    handleQuoteResponse.quoteResponse = result;

                    // const jumpLocationDueToUWIssue = await handleUWIssues(productCode, whichPage, jobNumber, result, jobType);

                    // if(!_.isUndefined(jumpLocationDueToUWIssue)){
                    //     handleQuoteResponse.jumpLocation = jumpLocationDueToUWIssue;
                    // }
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                }).catch(async (error) => {
                    await showExceptionMessage();
                    // handleQuoteResponse.jumpLocation = `/producer-engage/change/${jobNumber}/summary`;
                    handleQuoteResponse.jumpLocation = `/change/${jobNumber}/summary`;
                 
                }).finally(() => {
                    return handleQuoteResponse;
                });

        } else if(submissionVMValue.baseData.jobType === 'Submission') { 
            const response = await LoadSaveService.saveAndQuoteSubmission( submissionVMValue, authHeader)
                .then(async (result) => {
                    handleQuoteResponse.quoteResponse = result;
					return handleQuoteResponse					  
                });

            return response
        } else if(submissionVMValue.baseData.jobType === 'Renewal') { 
            await LoadSaveService.saveAndQuoteSubmission( submissionVMValue, authHeader)
                .then(async (result) => {
                    handleQuoteResponse.quoteResponse = result;

                    const jumpLocationDueToUWIssue = await handleUWIssues(productCode, whichPage, jobNumber, result, jobType);

                    if(!_.isUndefined(jumpLocationDueToUWIssue)){
                        handleQuoteResponse.jumpLocation = jumpLocationDueToUWIssue;
                    }
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                }).catch(async (error) => {
                    await showExceptionMessage();
                    // handleQuoteResponse.jumpLocation = `/producer-engage/renewal/${jobNumber}/summary`;
                    handleQuoteResponse.jumpLocation = `/renewal/${jobNumber}/summary`;
                }).finally(() => {
                    return handleQuoteResponse;
                });
        }

        return handleQuoteResponse;
    
    }, [EndorsementService, LoadSaveService, authHeader, handleUWIssues, showExceptionMessage]);

    const setErrorModalFromWhitelist = (error) => {
        let errorModal;
        let errorWhiteListMessage = ':';
        const isWhitelistedErrorPresent = definedErrors.errors.some((whiteListedError) => {
            const whitelistedErrorKey = Object.keys(whiteListedError)[0];

            if (error.baseError && error.baseError.error
                && error.baseError.error.message.includes(whitelistedErrorKey)) {
                // eslint-disable-next-line security/detect-object-injection
                errorWhiteListMessage = whiteListedError[whitelistedErrorKey];

                return true;
            }

            return false;
        });
    
        if (isWhitelistedErrorPresent) {
            errorModal = showAlert({
                title: (commonMessages.unableToGenerateAQuote),
                message: (errorWhiteListMessage),
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            });
        } else {
            errorModal = showAlert({
                title: (commonMessages.unableToGenerateAQuote),
                message: (commonMessages.somethingWentWrong),
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            });
        }

        return errorModal;
    };

    return { validateSubmission, updateSubmission, handleUWIssues, handleBindUWIssues, handleBindProcess, saveAndQuoteSubmissionService, setErrorModalFromWhitelist };
}

export { useUpdateSubmissionServices };